import React, { ReactNode } from 'react';
import { vars } from '../../theme';
import { TFontWeight } from '../HBField/HBField.types';
import { Label } from './HBLabel.styles';
import { useBuildTestIdInternal } from '../../hooks/useBuildTestIdInternal';
import { MustIncludeOneOfBasicTestIdWithIdProps } from '../common/internal/test-id.types';

export interface Props {
    id?: string;
    /** Label content; when a string, an asterisk is automatically appended if `isRequired` is true. */
    text: ReactNode;
    /** Applies muted styling to indicate the associated form control is disabled. */
    disabled?: boolean;
    /** CSS class applied to the root element. */
    className?: string;
    /** Appends an asterisk (*) to string labels to indicate required fields. */
    isRequired?: boolean;
    /** Controls the label text size. Defaults to `'sm'`. */
    size?: 'xxs' | 'xs' | 'sm' | 'md' | 'lg' | 'xl';
    /** Text color. Defaults to `vars.colors.text.primary`. */
    color?: string;
    /** Transforms the label text to uppercase. */
    uppercase?: boolean;
    /** Font weight of the label text. `'normal'` for supporting context, `'semibold'` for default field labels, `'bold'` for primary headings. Defaults to `'normal'`. */
    fontWeight?: TFontWeight;
    testId?: string;
    /** When true, renders as an element with `id` instead of a `<label>` with `htmlFor`, for use with fieldset/group patterns. */
    isGroupLabel?: boolean;
}

const HBLabel = ({
    id,
    testIdName,
    testId,
    className,
    text,
    isRequired,
    disabled,
    size = 'sm',
    color = vars.colors.text.primary,
    uppercase = false,
    fontWeight = 'normal',
    isGroupLabel = false
}: Props & MustIncludeOneOfBasicTestIdWithIdProps) => {
    const label =
        typeof text === 'string' ? `${text} ${isRequired ? '*' : ''}` : text;

    const finalTestId = useBuildTestIdInternal({
        testId,
        identifier: testIdName || id,
        suffix: 'label',
        componentName: 'HBLabel'
    });

    return (
        <Label
            {...(isGroupLabel ? { id } : { htmlFor: id })}
            size={size}
            disabled={disabled}
            className={className}
            color={color}
            fontWeight={fontWeight}
            uppercase={uppercase}
            data-testid={finalTestId}
        >
            {label}
        </Label>
    );
};

export default HBLabel;
